/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Event } from '../../../base/common/event.js';
import { Disposable } from '../../../base/common/lifecycle.js';
import { IPCServer, IServerChannel } from '../../../base/parts/ipc/common/ipc.js';
import { ILoggerService } from '../../log/common/log.js';
import { IGatewayCallToolResult, IGatewayServerResources, IGatewayServerResourceTemplates, IMcpGatewayService, McpGatewayToolBrokerChannelName } from '../common/mcpGateway.js';
import { MCP } from '../common/modelContextProtocol.js';

/**
 * IPC channel for the MCP Gateway service, used by the remote server.
 *
 * This channel tracks which client (identified by reconnectionToken) creates gateways,
 * enabling cleanup when a client disconnects.
 */
export class McpGatewayChannel<TContext> extends Disposable implements IServerChannel<TContext> {

	constructor(
		private readonly _ipcServer: IPCServer<TContext>,
		@IMcpGatewayService private readonly mcpGatewayService: IMcpGatewayService,
		@ILoggerService private readonly _loggerService: ILoggerService,
	) {
		super();
		this._register(_ipcServer.onDidRemoveConnection(c => {
			this._loggerService.getLogger('mcpGateway')?.info(`[McpGateway][Channel] Client disconnected: ${c.ctx}, cleaning up gateways`);
			mcpGatewayService.disposeGatewaysForClient(c.ctx);
		}));
	}

	listen<T>(_ctx: TContext, _event: string): Event<T> {
		throw new Error('Invalid listen');
	}

	async call<T>(ctx: TContext, command: string, args?: unknown): Promise<T> {
		const logger = this._loggerService.getLogger('mcpGateway');
		logger?.debug(`[McpGateway][Channel] IPC call: ${command} from client ${ctx}`);

		switch (command) {
			case 'createGateway': {
				const brokerChannel = ipcChannelForContext(this._ipcServer, ctx);
				const result = await this.mcpGatewayService.createGateway(ctx, {
					onDidChangeTools: brokerChannel.listen<void>('onDidChangeTools'),
					onDidChangeResources: brokerChannel.listen<void>('onDidChangeResources'),
					listTools: () => brokerChannel.call<readonly MCP.Tool[]>('listTools'),
					callTool: (name, callArgs) => brokerChannel.call<IGatewayCallToolResult>('callTool', { name, args: callArgs }),
					listResources: () => brokerChannel.call<readonly IGatewayServerResources[]>('listResources'),
					readResource: (serverIndex, uri) => brokerChannel.call<MCP.ReadResourceResult>('readResource', { serverIndex, uri }),
					listResourceTemplates: () => brokerChannel.call<readonly IGatewayServerResourceTemplates[]>('listResourceTemplates'),
				});
				logger?.info(`[McpGateway][Channel] Gateway created: ${result.gatewayId} for client ${ctx}`);
				return result as T;
			}
			case 'disposeGateway': {
				logger?.info(`[McpGateway][Channel] Disposing gateway: ${args as string} for client ${ctx}`);
				await this.mcpGatewayService.disposeGateway(args as string);
				return undefined as T;
			}
		}

		throw new Error(`Invalid call: ${command}`);
	}
}

function ipcChannelForContext<TContext>(ipcServer: IPCServer<TContext>, ctx: TContext) {
	return ipcServer.getChannel(McpGatewayToolBrokerChannelName, client => client.ctx === ctx);
}
